import { useState } from "react";import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Turnstile from "../components/Turnstile";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tmpToken, setTmpToken] = useState(null);
  const [code, setCode] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await login(email, password, captchaToken);
      if (res && res.requires_2fa) {
        setTmpToken(res.tmp_token);
        toast.message("Enter your 2FA code to continue");
      } else {
        toast.success("Welcome back to Fx Cloud");
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await verify2fa(tmpToken, code);
      toast.success("Verified — welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell min-h-screen grid lg:grid-cols-2 bg-fx-base">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-fx-base via-fx-base/70 to-transparent" />
        <Link to="/" className="relative flex items-center gap-2 text-white">
          <div className="h-9 w-9 rounded-lg bg-brandblue grid place-items-center"><TrendingUp className="h-5 w-5" /></div>
          <span className="font-display font-extrabold text-xl tracking-tight">Fx Cloud</span>
        </Link>
        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight">Trade smarter.<br />Practice risk-free.</h2>
          <p className="text-fx-s3 mt-4 max-w-md">Master the markets with a full demo environment, AI trading bots and a professional terminal — before you risk a cent.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-brandblue grid place-items-center"><TrendingUp className="h-5 w-5" /></div>
            <span className="font-display font-extrabold text-xl">Fx Cloud</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{tmpToken ? "Two-factor authentication" : "Sign in"}</h1>
          <p className="text-fx-s3 mt-2 text-sm">{tmpToken ? "Enter the 6-digit code from your authenticator app (or a backup code)." : "Enter your credentials to access your account."}</p>

          {tmpToken ? (
            <form onSubmit={submit2fa} className="mt-8 space-y-4">
              {error && <div data-testid="login-error" role="alert" className="rounded-lg bg-neg/10 border border-neg/30 text-neg text-sm px-3 py-2">{error}</div>}
              <div className="space-y-2">
                <Label htmlFor="code">Authentication code</Label>
                <Input id="code" data-testid="twofa-code-input" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="h-11 bg-fx-s1 border-white/10 font-mono tracking-widest text-center text-lg" />
              </div>
              <Button type="submit" disabled={loading} data-testid="twofa-verify-button" className="w-full h-11 bg-brandblue hover:bg-blue-500 text-white font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => { setTmpToken(null); setCode(""); setError(""); }} className="w-full text-sm text-fx-s3 hover:text-white">Back to login</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {error && (
              <div data-testid="login-error" role="alert" className="rounded-lg bg-neg/10 border border-neg/30 text-neg text-sm px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 bg-fx-s1 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-11 bg-fx-s1 border-white/10" />
            </div>
            <Turnstile onToken={setCaptchaToken} />
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 bg-brandblue hover:bg-blue-500 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          )}

          {!tmpToken && (<>

          <p className="text-center text-sm text-fx-s3 mt-8">
            No account?{" "}
            <Link to="/register" data-testid="go-register-link" className="text-brandblue hover:underline">Create one</Link>
          </p>
          </>)}
        </div>
      </div>
    </div>
  );
}
